import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import { apiGet } from './api';

echarts.use([LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

const periodOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
];

const metricOptions = [
    { value: 'total_value_usd', label: 'Total USD' },
    { value: 'total_value_inr', label: 'Total INR' },
    { value: 'total_quantity', label: 'Quantity' },
    { value: 'transaction_count', label: 'Transactions' },
    { value: 'avg_unit_price_usd', label: 'Avg Unit USD' },
    { value: 'avg_unit_price_inr', label: 'Avg Unit INR' },
];

const dimensionOptions = [
    { value: 'overall', label: 'Overall Import vs Export' },
    { value: 'category', label: 'Category' },
    { value: 'country', label: 'Country' },
    { value: 'hsn', label: 'HSN' },
    { value: 'importer', label: 'Importer' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'exporter', label: 'Exporter' },
    { value: 'buyer', label: 'Buyer' },
    { value: 'indian_port', label: 'Indian Port' },
    { value: 'shipment_port', label: 'Shipment / Foreign Port' },
];

const formatValue = (value, metric) => {
    const number = Number(value || 0);
    if (metric.includes('usd') || metric.includes('inr')) {
        return number.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    }
    return number.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const TrendChart = ({ trend }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const periods = useMemo(() => trend?.periods || [], [trend?.periods]);
    const series = useMemo(() => trend?.series || [], [trend?.series]);
    const metric = trend?.metric || '';
    const hasData = Boolean(trend && periods.length && series.length);

    useEffect(() => {
        if (!chartRef.current) return undefined;
        const chart = chartInstanceRef.current
            || echarts.getInstanceByDom(chartRef.current)
            || echarts.init(chartRef.current);
        chartInstanceRef.current = chart;

        if (!hasData) {
            chart.clear();
            return undefined;
        }

        const periodKeys = periods.map((period) => period.period_start);
        const periodLabels = periods.map((period) => period.period_label || period.period_start);

        chart.setOption({
            color: ['#1976d2', '#00897b', '#ef6c00', '#7b1fa2', '#c62828', '#5d4037', '#455a64', '#2e7d32', '#ad1457', '#0277bd'],
            grid: {
                left: 72,
                right: 24,
                top: 72,
                bottom: 72,
            },
            legend: {
                type: 'scroll',
                top: 8,
            },
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const label = params?.[0]?.axisValueLabel || '';
                    const lines = params.map((item) => (
                        `${item.marker}${item.seriesName}: ${formatValue(item.value, metric)}`
                    ));
                    return [label, ...lines].join('<br/>');
                },
            },
            xAxis: {
                type: 'category',
                data: periodLabels,
                axisLabel: {
                    rotate: periodLabels.length > 6 ? 35 : 0,
                    overflow: 'truncate',
                    width: 90,
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value) => {
                        if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
                        if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value;
                    },
                },
                splitLine: {
                    lineStyle: { type: [3, 4], color: '#d6d6d6' },
                },
            },
            series: series.map((item) => {
                const valuesByPeriod = new Map(item.points.map((point) => [point.period_start, point.value]));
                return {
                    name: item.name,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    data: periodKeys.map((periodKey) => valuesByPeriod.get(periodKey) || 0),
                };
            }),
        }, true);

        chart.resize();

        return undefined;
    }, [hasData, metric, periods, series]);

    useEffect(() => {
        const resize = () => chartInstanceRef.current?.resize();
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
            if (chartInstanceRef.current) {
                chartInstanceRef.current.dispose();
                chartInstanceRef.current = null;
            }
        };
    }, []);

    return (
        <Box sx={{ position: 'relative', height: 520, width: '100%' }}>
            <Box
                ref={chartRef}
                sx={{
                    height: 520,
                    width: '100%',
                    visibility: hasData ? 'visible' : 'hidden',
                }}
            />
            {!hasData && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography color="text.secondary">No trend data for the selected filters</Typography>
                </Box>
            )}
        </Box>
    );
};

const ImportTrendDashboard = () => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        period: 'monthly',
        metric: 'total_value_usd',
        trade_direction: '',
        dimension: 'overall',
        dimension_ids: [],
        from_date: '',
        to_date: '',
    });
    const [optionSearch, setOptionSearch] = useState('');
    const [options, setOptions] = useState([]);
    const [trend, setTrend] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const selectedOptionNames = useMemo(() => {
        const byId = new Map(options.map((option) => [String(option.id), option.name]));
        return filters.dimension_ids.map((id) => byId.get(String(id)) || id);
    }, [filters.dimension_ids, options]);

    const fetchOptions = async () => {
        if (filters.dimension === 'overall') {
            setOptions([]);
            return;
        }
        try {
            const response = await apiGet('/import/trend-options', {
                dimension: filters.dimension,
                q: optionSearch,
                limit: 200,
            });
            setOptions(response.json || []);
        } catch (err) {
            setOptions([]);
        }
    };

    const fetchTrend = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiGet('/import/trends', {
                period: filters.period,
                metric: filters.metric,
                trade_direction: filters.trade_direction,
                dimension: filters.dimension,
                dimension_ids: filters.dimension_ids.join(','),
                from_date: filters.from_date,
                to_date: filters.to_date,
            });
            setTrend(response.json);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrend();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.dimension, optionSearch]);

    const updateFilter = (key, value) => {
        setFilters((current) => ({
            ...current,
            [key]: value,
            ...(key === 'dimension' ? { dimension_ids: [] } : {}),
        }));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6">Import/Export Trend Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Compare import and export movement across time, countries, HSN codes, ports, parties, and categories.
                    </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/import')}>
                    Summary
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Period</InputLabel>
                        <Select
                            label="Period"
                            value={filters.period}
                            onChange={(event) => updateFilter('period', event.target.value)}
                        >
                            {periodOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Metric</InputLabel>
                        <Select
                            label="Metric"
                            value={filters.metric}
                            onChange={(event) => updateFilter('metric', event.target.value)}
                        >
                            {metricOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Direction</InputLabel>
                        <Select
                            label="Direction"
                            value={filters.trade_direction}
                            onChange={(event) => updateFilter('trade_direction', event.target.value)}
                        >
                            <MenuItem value="">Import + Export</MenuItem>
                            <MenuItem value="IMPORT">Import</MenuItem>
                            <MenuItem value="EXPORT">Export</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Dimension</InputLabel>
                        <Select
                            label="Dimension"
                            value={filters.dimension}
                            onChange={(event) => updateFilter('dimension', event.target.value)}
                        >
                            {dimensionOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={filters.from_date}
                        onChange={(event) => updateFilter('from_date', event.target.value)}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={filters.to_date}
                        onChange={(event) => updateFilter('to_date', event.target.value)}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<ShowChartIcon />}
                        onClick={fetchTrend}
                        disabled={loading}
                    >
                        Apply
                    </Button>
                </Stack>

                {filters.dimension !== 'overall' && (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            size="small"
                            label="Search values"
                            value={optionSearch}
                            onChange={(event) => setOptionSearch(event.target.value)}
                            sx={{ minWidth: 260 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 360, maxWidth: 620 }}>
                            <InputLabel>Selected values</InputLabel>
                            <Select
                                multiple
                                label="Selected values"
                                value={filters.dimension_ids}
                                renderValue={() => selectedOptionNames.length ? selectedOptionNames.join(', ') : 'Top 10 by selected metric'}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    updateFilter(
                                        'dimension_ids',
                                        typeof value === 'string'
                                            ? value.split(',').map(Number).filter(Boolean)
                                            : value.map(Number).filter(Boolean)
                                    );
                                }}
                            >
                                {options.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox checked={filters.dimension_ids.map(String).includes(String(option.id))} />
                                        <ListItemText primary={option.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button size="small" onClick={() => updateFilter('dimension_ids', [])}>
                            Clear Values
                        </Button>
                    </Stack>
                )}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="h6">{trend?.metricLabel || 'Trend'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {trend?.dimensionLabel || 'Overall'} by {filters.period}; blank value selection uses top 10 series for that dimension.
                    </Typography>
                </Box>
                <TrendChart trend={trend} />
            </Paper>
        </Box>
    );
};

export default ImportTrendDashboard;
